import { IAutopickPreferences } from "core-integration/src/store/squad/types";
import { TFunction } from "i18next";
import {
  AutoPickChoice,
  BudgetAllocationChoice,
  PositionFocusChoice,
  PreferFavouriteTeamChoice,
  TeamBuilderPreferencesFormData,
} from "./types";

export type ExtendedLabelFormOptions<T extends string = string> = {
  value: T;
  label: string;
  extendedLabel: string;
};

export const getDefaultTeamName = (
  t: TFunction<"onboarding">,
  firstName: string | undefined,
) => {
  if (!firstName) return "";
  const teamName = t("defaultTeamName", "{{firstName}}'s Team", {
    firstName,
  }) as string;
  // Prevent long first names from exceeding the character limit
  return teamName.length <= 20 ? teamName : t("myTeam", "My Team");
};

// Resolve the favourite club prompt, tailored to the user's selected team.
// Uses literal t() calls so i18next-parser can extract the keys.
export const getFavouriteTeamSubtitle = (
  t: TFunction<"onboarding">,
  teamName?: string,
) =>
  teamName
    ? t(
        "preferFavouriteTeamLabel",
        "Your favourite club is {{team}}. Would you like to have {{team}} players in your initial squad?",
        { team: teamName },
      )
    : t(
        "noFavouriteTeamLabel",
        "As you have not selected a favourite club, the autopick will not favour any specific club.",
      );

// Option to determine tutorial process

export const getTutorialChoices = (
  t: TFunction<"onboarding">,
): ExtendedLabelFormOptions<AutoPickChoice>[] => [
  {
    value: "show_tutorial",
    label: t("tutorialChoice.yes", "Yes, help me out"),
    extendedLabel: t(
      "tutorialChoice.yesExtended",
      "We'll take you through a series of questions to help build your squad.",
    ),
  },
  {
    value: "manual_pick",
    label: t("tutorialChoice.no", "No thanks, I'll go solo"),
    extendedLabel: t(
      "tutorialChoice.noExtended",
      "I'm ready to select my squad.",
    ),
  },
];

// Options for autopick preferences in the tutorial
export const getPreferFavouriteTeamChoices = (
  t: TFunction<"onboarding">,
): ExtendedLabelFormOptions<PreferFavouriteTeamChoice>[] => [
  {
    value: "yes",
    label: t("preferFavouriteTeamChoice.yes", "Yes"),
    extendedLabel: t(
      "preferFavouriteTeamChoice.yesExtended",
      "At least 2 players in your squad will be from this club.",
    ),
  },
  {
    value: "no",
    label: t("preferFavouriteTeamChoice.no", "Don't mind"),
    extendedLabel: t(
      "preferFavouriteTeamChoice.noExtended",
      "You may not have players from this club in your squad.",
    ),
  },
];

export const getBudgetAllocationChoices = (
  t: TFunction<"onboarding">,
): ExtendedLabelFormOptions<BudgetAllocationChoice>[] => [
  {
    value: "balanced_spend",
    label: t("budgetAllocationChoice.balancedSpend", "Balanced Spend"),
    extendedLabel: t(
      "budgetAllocationChoice.balancedSpendExtended",
      "Balance the spend across your starting 11 and bench, allows for more options.",
    ),
  },
  {
    value: "strong_starting_xi",
    label: t("budgetAllocationChoice.strongStartingXI", "Stronger Starting 11"),
    extendedLabel: t(
      "budgetAllocationChoice.strongStartingXIExtended",
      "Prioritise 11 regular starters with minimal spend on benched players. A stronger 11, but fewer options.",
    ),
  },
];

export const getPositionFocusChoices = (
  t: TFunction<"onboarding">,
): ExtendedLabelFormOptions<PositionFocusChoice>[] => [
  {
    value: "defensive_focus",
    label: t("positionFocusChoice.defensiveFocus", "Defensive Focus"),
    extendedLabel: t(
      "positionFocusChoice.defensiveFocusExtended",
      "Prioritise goalkeepers and defenders for points from clean sheets.",
    ),
  },
  {
    value: "balanced_squad",
    label: t("positionFocusChoice.balancedSquad", "Balanced Squad"),
    extendedLabel: t(
      "positionFocusChoice.balancedSquadExtended",
      "Spread your budget evenly across defence, midfield and attack.",
    ),
  },
  {
    value: "attacking_focus",
    label: t("positionFocusChoice.attackingFocus", "Attacking Focus"),
    extendedLabel: t(
      "positionFocusChoice.attackingFocusExtended",
      "Prioritise midfielders and forwards for goals and assists.",
    ),
  },
];

// Backend autopick request codes (see plfpl/game/serializers/autopick.py).
const spendFocusStrongFirstXi = 1;
const spendFocusSpread = 2;
const attackSkewAttack = 1;
const attackSkewBalanced = 2;
const attackSkewDefensive = 3;

// Map the collected tutorial survey answers to the backend autopick payload.
// The backend exposes only attack/balanced for skew, so "defensive_focus"
// (no backend equivalent) falls back to the balanced bias.
export const getAutopickPreferences = (
  prefs: TeamBuilderPreferencesFormData,
  favouriteTeam: number | null,
): IAutopickPreferences => {
  const { preferFavouriteTeam, budgetAllocation, positionFocus } = prefs;

  const team = Number(favouriteTeam);
  const clubPreference =
    preferFavouriteTeam === "yes" && team > 0 ? team : null;
  let attackSkew = attackSkewBalanced;
  if (positionFocus === "attacking_focus") {
    attackSkew = attackSkewAttack;
  } else if (positionFocus === "defensive_focus") {
    attackSkew = attackSkewDefensive;
  }

  return {
    club_preference: clubPreference,
    spend_focus:
      budgetAllocation === "strong_starting_xi"
        ? spendFocusStrongFirstXi
        : spendFocusSpread,
    attack_skew: attackSkew,
  };
};
